// Admin Settings > Platform Master > Referral Program (D30 PR-D).
//
// The single admin console for the referral program, on the REAL contracts:
//
//   Program defaults (T-N26 hybrid — the numbers Volkan edits):
//     GET /api/v1/admin/config                     (referral.* whitelist keys)
//     PUT /api/v1/admin/config/{key}               ({"value": <n>}, numeric)
//
//   Referral codes (BE-202 governance):
//     GET    /api/v1/admin/referral/codes
//     POST   /api/v1/admin/referral/codes          (user picked from directory)
//     PATCH  /api/v1/admin/referral/codes/{id}     (per-code overrides)
//     DELETE /api/v1/admin/referral/codes/{id}     (deactivate)
//
//   T-N27 activation approvals:
//     GET  /api/v1/admin/referral/events?event_kind=activation
//     POST /api/v1/admin/referral/activations/{id}/approve
//
//   Rewards program terms (rewards part 2, migration 398):
//     GET/POST /api/v1/admin/rewards/terms        (versioned numbers, see RewardsTermsCard)
//
// Unset defaults fall back to the engine's built-in floors (3% / 365 days);
// saving here creates the admin_config_kv rows — no migration seed needed.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { DataTableShell } from '../../components/ui/data-table-shell'
import { apiUrl } from '../../lib/api'
import { formatDate } from '../../lib/datetime'
import { useT } from '../../i18n'
import {
  referralProgramAdminI18n,
  type ReferralProgramAdminStrings,
} from '../../i18n/admin/referral-program-admin'
import { RewardsTermsCard } from './rewards/RewardsTermsCard'

const FALLBACKS: Record<string, number> = {
  'referral.commission_percent_default': 3,
  'referral.welcome_bonus_usd_default': 50,
  'referral.commission_period_days_default': 365,
}

const DEFAULT_FIELDS: Array<{
  key: string
  field: keyof ReferralProgramAdminStrings['fields']
}> = [
  { key: 'referral.commission_percent_default', field: 'commission' },
  { key: 'referral.commission_period_days_default', field: 'period' },
  { key: 'referral.welcome_bonus_usd_default', field: 'bonus' },
]

type ConfigListItem = {
  config_key: string
  defined: boolean
  description: string | null
  value: { config_value: { value?: number | string } } | null
}

type ReferralCodeRow = {
  id: string
  user_id: string
  code: string
  commission_percent_override: string | number | null
  welcome_bonus_usd_override: string | number | null
  commission_period_days_override: number | null
  is_active: boolean
  created_at: string
  notes: string | null
}

type ReferralEventRow = {
  id: string
  referrer_user_id: string
  referee_user_id: string | null
  referral_code_id: string
  event_kind: string
  referee_email_masked: string | null
  event_payload: Record<string, unknown>
  triggered_at: string
}

type DirectoryUser = {
  user_id: string
  user_label: string
  email: string | null
  org_name: string
  org_type: string
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(apiUrl(path))
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

async function sendJson<T>(path: string, method: string, body?: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method,
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as {
      detail?: { message?: string; code?: string }
    }
    throw new Error(err.detail?.message ?? `HTTP ${res.status}`)
  }
  return res.json()
}

function toNum(v: unknown): number | null {
  const n = typeof v === 'string' ? Number.parseFloat(v) : typeof v === 'number' ? v : NaN
  return Number.isFinite(n) ? n : null
}

// The /apply funnel lives in the main app, not on the admin host — so the
// shareable link is built on the app's public base, never window.origin here.
const APP_PUBLIC_BASE =
  ((import.meta.env.VITE_APP_PUBLIC_BASE as string | undefined) ?? 'https://app.pexify.net').replace(
    /\/$/,
    '',
  )

function shareLinkFor(code: string): string {
  return `${APP_PUBLIC_BASE}/apply?ref=${encodeURIComponent(code)}`
}

function fmtDate(iso: string): string {
  return formatDate(iso, { year: 'numeric', month: 'short', day: 'numeric' })
}

// ---------------------------------------------------------------------------
// Program defaults card — the admin-editable numbers
// ---------------------------------------------------------------------------

function DefaultsCard() {
  const t = useT(referralProgramAdminI18n)
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [savedFlash, setSavedFlash] = useState(false)
  const savedFlashTimerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (savedFlashTimerRef.current) window.clearTimeout(savedFlashTimerRef.current)
    }
  }, [])

  const configQuery = useQuery<{ items: ConfigListItem[] }>({
    queryKey: ['admin', 'config'],
    queryFn: () => getJson('/admin/config'),
  })

  const items = useMemo(() => {
    const byKey = new Map(
      (configQuery.data?.items ?? []).map((i) => [i.config_key, i] as const),
    )
    return DEFAULT_FIELDS.map((f) => {
      const item = byKey.get(f.key)
      const current = item?.defined ? toNum(item.value?.config_value?.value) : null
      return { ...f, ...t.fields[f.field], current, fallback: FALLBACKS[f.key] }
    })
  }, [configQuery.data, t])

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const item of items) {
        const raw = draft[item.key]
        if (raw === undefined || raw.trim() === '') continue
        const n = Number.parseFloat(raw)
        if (!Number.isFinite(n) || n < 0) throw new Error(t.defaults.invalidNumber(item.label))
        await sendJson(`/admin/config/${encodeURIComponent(item.key)}`, 'PUT', {
          config_value: { value: n },
          value_type: 'numeric',
        })
      }
    },
    onSuccess: () => {
      setDraft({})
      setSavedFlash(true)
      if (savedFlashTimerRef.current) window.clearTimeout(savedFlashTimerRef.current)
      savedFlashTimerRef.current = window.setTimeout(() => setSavedFlash(false), 2000)
      void queryClient.invalidateQueries({ queryKey: ['admin', 'config'] })
    },
  })

  const dirty = Object.values(draft).some((v) => v.trim() !== '')

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-slate-900">{t.defaults.title}</h2>
        <span className="text-xs text-slate-400">
          {t.defaults.subtitle}
        </span>
      </div>
      {configQuery.isLoading ? (
        <div className="py-6 text-sm text-slate-500">{t.loading}</div>
      ) : configQuery.isError ? (
        <div className="py-6 text-sm text-rose-700">{t.defaults.loadError}</div>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
            {items.map((item) => (
              <div key={item.key}>
                <label className="block text-xs font-medium text-slate-700">{item.label}</label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={draft[item.key] ?? ''}
                    placeholder={String(item.current ?? item.fallback)}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, [item.key]: e.target.value }))
                    }
                    className="w-28 rounded-md border border-slate-300 px-2 py-1.5 text-sm tabular-nums"
                  />
                  <span className="text-xs text-slate-500">{item.suffix}</span>
                </div>
                <div className="mt-1 text-[11px] text-slate-400">
                  {item.current == null
                    ? t.defaults.notSet(item.fallback)
                    : item.hint}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Button
              variant="primary"
              size="sm"
              disabled={!dirty || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? t.saving : t.defaults.save}
            </Button>
            {savedFlash && <span className="text-xs text-emerald-600">{t.defaults.saved}</span>}
            {saveMutation.isError && (
              <span className="text-xs text-rose-600">
                {String((saveMutation.error as Error).message)}
              </span>
            )}
          </div>
        </>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Codes card — issue / override / deactivate
// ---------------------------------------------------------------------------

function CodesCard() {
  const t = useT(referralProgramAdminI18n)
  const queryClient = useQueryClient()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [userFilter, setUserFilter] = useState('')
  const [selectedUser, setSelectedUser] = useState<DirectoryUser | null>(null)
  const [editing, setEditing] = useState<ReferralCodeRow | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [justIssued, setJustIssued] = useState<ReferralCodeRow | null>(null)
  const [overrideDraft, setOverrideDraft] = useState<{
    percent: string
    days: string
    bonus: string
  }>({ percent: '', days: '', bonus: '' })
  const copiedTimerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current)
    }
  }, [])

  async function copyLink(row: ReferralCodeRow) {
    try {
      await navigator.clipboard.writeText(shareLinkFor(row.code))
      setCopiedId(row.id)
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current)
      copiedTimerRef.current = window.setTimeout(
        () => setCopiedId((c) => (c === row.id ? null : c)),
        1800,
      )
    } catch {
      // clipboard requires secure context — the visible link still allows manual copy
    }
  }

  const codesQuery = useQuery<{ items: ReferralCodeRow[]; total: number }>({
    queryKey: ['admin', 'referral', 'codes'],
    queryFn: () => getJson('/admin/referral/codes?limit=200'),
  })
  const directoryQuery = useQuery<{ users: DirectoryUser[] }>({
    queryKey: ['admin', 'users', 'directory'],
    queryFn: () => getJson('/admin/users/directory'),
    enabled: pickerOpen,
  })

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['admin', 'referral', 'codes'] })

  const generateMutation = useMutation({
    mutationFn: (userId: string) =>
      sendJson<ReferralCodeRow>('/admin/referral/codes', 'POST', { user_id: userId }),
    onSuccess: (row) => {
      setPickerOpen(false)
      setSelectedUser(null)
      setUserFilter('')
      setJustIssued(row)
      invalidate()
    },
  })
  const overrideMutation = useMutation({
    mutationFn: (row: ReferralCodeRow) => {
      const body: Record<string, unknown> = {}
      if (overrideDraft.percent.trim() !== '')
        body.commission_percent_override = Number.parseFloat(overrideDraft.percent)
      if (overrideDraft.days.trim() !== '')
        body.commission_period_days_override = Number.parseInt(overrideDraft.days, 10)
      if (overrideDraft.bonus.trim() !== '')
        body.welcome_bonus_usd_override = Number.parseFloat(overrideDraft.bonus)
      return sendJson(`/admin/referral/codes/${row.id}`, 'PATCH', body)
    },
    onSuccess: () => {
      setEditing(null)
      setOverrideDraft({ percent: '', days: '', bonus: '' })
      invalidate()
    },
  })
  const deactivateMutation = useMutation({
    mutationFn: (row: ReferralCodeRow) =>
      sendJson(`/admin/referral/codes/${row.id}`, 'DELETE'),
    onSuccess: invalidate,
  })

  const filteredUsers = (directoryQuery.data?.users ?? []).filter((u) => {
    const q = userFilter.trim().toLowerCase()
    if (!q) return true
    return (
      u.user_label.toLowerCase().includes(q) ||
      (u.email ?? '').toLowerCase().includes(q) ||
      u.org_name.toLowerCase().includes(q)
    )
  })

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900">{t.codes.title}</h2>
        <Button variant="primary" size="sm" onClick={() => setPickerOpen((o) => !o)}>
          {pickerOpen ? t.codes.cancel : t.codes.issueCodeToggle}
        </Button>
      </div>

      {pickerOpen && (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
          <label className="block text-xs font-medium text-slate-700">
            {t.codes.issueTo}
          </label>
          <input
            type="text"
            value={userFilter}
            onChange={(e) => {
              setUserFilter(e.target.value)
              setSelectedUser(null)
            }}
            placeholder={t.codes.searchPlaceholder}
            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          />
          {directoryQuery.isLoading ? (
            <div className="py-3 text-xs text-slate-500">{t.codes.loadingDirectory}</div>
          ) : (
            <div className="mt-2 max-h-44 overflow-y-auto divide-y divide-slate-200 rounded border border-slate-200 bg-white">
              {filteredUsers.slice(0, 30).map((u) => (
                <button
                  key={`${u.user_id}-${u.org_name}`}
                  type="button"
                  onClick={() => setSelectedUser(u)}
                  className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-indigo-50 ${
                    selectedUser?.user_id === u.user_id ? 'bg-indigo-50' : ''
                  }`}
                >
                  <span className="font-medium text-slate-800">{u.user_label}</span>{' '}
                  <span className="text-slate-500">
                    {u.email ?? ''} · {u.org_name} ({u.org_type})
                  </span>
                </button>
              ))}
              {filteredUsers.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-400">{t.codes.noMatches}</div>
              )}
            </div>
          )}
          <div className="mt-2 flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={!selectedUser || generateMutation.isPending}
              onClick={() => selectedUser && generateMutation.mutate(selectedUser.user_id)}
            >
              {generateMutation.isPending ? t.codes.issuing : t.codes.issueCode}
            </Button>
            {generateMutation.isError && (
              <span className="text-xs text-rose-600">
                {String((generateMutation.error as Error).message)}
              </span>
            )}
          </div>
        </div>
      )}

      {justIssued && (
        <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-xs font-medium text-emerald-800">
            {t.codes.codeIssued} <span className="font-mono">{justIssued.code}</span>{' '}
            {t.codes.shareThisLink}
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <div className="flex-1 rounded-md border border-emerald-200 bg-white px-2 py-1.5 text-xs font-mono text-slate-700 truncate">
              {shareLinkFor(justIssued.code)}
            </div>
            <Button variant="outline" size="sm" onClick={() => copyLink(justIssued)}>
              {copiedId === justIssued.id ? t.copied : t.copyLink}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setJustIssued(null)}>
              {t.codes.dismiss}
            </Button>
          </div>
        </div>
      )}

      <div className="mt-3">
        {codesQuery.isLoading ? (
          <div className="py-6 text-sm text-slate-500">{t.loading}</div>
        ) : (codesQuery.data?.items ?? []).length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">
            {t.codes.empty}
          </div>
        ) : (
          <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th className="py-2">{t.codes.columns.code}</th>
                <th className="py-2">{t.codes.columns.status}</th>
                <th className="py-2 text-right">{t.codes.columns.commission}</th>
                <th className="py-2 text-right">{t.codes.columns.period}</th>
                <th className="py-2 text-right">{t.codes.columns.bonus}</th>
                <th className="py-2">{t.codes.columns.issued}</th>
                <th className="py-2 text-right">{t.codes.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {(codesQuery.data?.items ?? []).map((row) => (
                <tr key={row.id} className="align-top">
                  <td className="py-2 font-mono text-xs text-[var(--color-text)]">{row.code}</td>
                  <td className="py-2">
                    <Badge variant={row.is_active ? 'success' : 'destructive'}>
                      {row.is_active ? t.codes.active : t.codes.deactivated}
                    </Badge>
                  </td>
                  <td className="py-2 text-right tabular-nums text-xs">
                    {row.commission_percent_override != null
                      ? `${row.commission_percent_override}%`
                      : t.defaultValue}
                  </td>
                  <td className="py-2 text-right tabular-nums text-xs">
                    {row.commission_period_days_override != null
                      ? t.codes.daysShort(row.commission_period_days_override)
                      : t.defaultValue}
                  </td>
                  <td className="py-2 text-right tabular-nums text-xs">
                    {row.welcome_bonus_usd_override != null
                      ? `$${row.welcome_bonus_usd_override}`
                      : t.defaultValue}
                  </td>
                  <td className="py-2 text-xs text-[var(--color-text-muted)] whitespace-nowrap">
                    {fmtDate(row.created_at)}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {row.is_active && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          title={shareLinkFor(row.code)}
                          onClick={() => copyLink(row)}
                        >
                          {copiedId === row.id ? t.copied : t.copyLink}
                        </Button>{' '}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditing(editing?.id === row.id ? null : row)
                            setOverrideDraft({ percent: '', days: '', bonus: '' })
                          }}
                        >
                          {t.codes.override}
                        </Button>{' '}
                        <Button
                          variant="outlineDestructive"
                          size="sm"
                          disabled={deactivateMutation.isPending}
                          onClick={() => deactivateMutation.mutate(row)}
                        >
                          {t.codes.deactivate}
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </DataTableShell>
        )}
      </div>

      {editing && (
        <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50/40 p-3">
          <div className="text-xs font-medium text-slate-700">
            {t.codes.customTermsFor} <span className="font-mono">{editing.code}</span>{' '}
            {t.codes.customTermsHint}
          </div>
          <div className="mt-2 flex flex-wrap items-end gap-3">
            <label className="text-xs text-slate-600">
              {t.codes.commissionPercent}
              <input
                type="number"
                min={0}
                step="any"
                value={overrideDraft.percent}
                placeholder={String(editing.commission_percent_override ?? t.defaultValue)}
                onChange={(e) =>
                  setOverrideDraft((d) => ({ ...d, percent: e.target.value }))
                }
                className="mt-0.5 block w-24 rounded-md border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="text-xs text-slate-600">
              {t.codes.periodDays}
              <input
                type="number"
                min={1}
                value={overrideDraft.days}
                placeholder={String(editing.commission_period_days_override ?? t.defaultValue)}
                onChange={(e) => setOverrideDraft((d) => ({ ...d, days: e.target.value }))}
                className="mt-0.5 block w-24 rounded-md border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="text-xs text-slate-600">
              {t.codes.bonusUsd}
              <input
                type="number"
                min={0}
                step="any"
                value={overrideDraft.bonus}
                placeholder={String(editing.welcome_bonus_usd_override ?? t.defaultValue)}
                onChange={(e) => setOverrideDraft((d) => ({ ...d, bonus: e.target.value }))}
                className="mt-0.5 block w-24 rounded-md border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <Button
              variant="primary"
              size="sm"
              disabled={overrideMutation.isPending}
              onClick={() => overrideMutation.mutate(editing)}
            >
              {overrideMutation.isPending ? t.saving : t.codes.saveOverrides}
            </Button>
          </div>
          {overrideMutation.isError && (
            <div className="mt-1 text-xs text-rose-600">
              {String((overrideMutation.error as Error).message)}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Activations card — the T-N27 approval gate
// ---------------------------------------------------------------------------

function ActivationsCard() {
  const t = useT(referralProgramAdminI18n)
  const queryClient = useQueryClient()
  const eventsQuery = useQuery<{ items: ReferralEventRow[]; total: number }>({
    queryKey: ['admin', 'referral', 'activations'],
    queryFn: () => getJson('/admin/referral/events?event_kind=activation&limit=200'),
  })

  const approveMutation = useMutation({
    mutationFn: (eventId: string) =>
      sendJson(`/admin/referral/activations/${eventId}/approve`, 'POST', {}),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['admin', 'referral', 'activations'] }),
  })

  // Group activation rows per (code, referee): the admin approve appends a NEW
  // approved=true row, so a referee is "pending" only while no row is approved.
  const pending = useMemo(() => {
    const items = eventsQuery.data?.items ?? []
    const byReferee = new Map<string, { anchor: ReferralEventRow; approved: boolean }>()
    const sorted = [...items].sort(
      (a, b) => new Date(a.triggered_at).getTime() - new Date(b.triggered_at).getTime(),
    )
    for (const e of sorted) {
      const key = `${e.referral_code_id}:${e.referee_user_id ?? e.id}`
      const g = byReferee.get(key)
      if (!g) {
        byReferee.set(key, { anchor: e, approved: Boolean(e.event_payload?.approved) })
      } else {
        g.approved = g.approved || Boolean(e.event_payload?.approved)
      }
    }
    return [...byReferee.values()].filter((g) => !g.approved)
  }, [eventsQuery.data])

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-slate-900">{t.activations.title}</h2>
        <span className="text-xs text-slate-400">
          {t.activations.subtitle}
        </span>
      </div>
      {eventsQuery.isLoading ? (
        <div className="py-6 text-sm text-slate-500">{t.loading}</div>
      ) : pending.length === 0 ? (
        <div className="py-6 text-center text-sm text-slate-500">
          {t.activations.empty}
        </div>
      ) : (
        <ul className="mt-3 divide-y divide-slate-200">
          {pending.map(({ anchor }) => (
            <li key={anchor.id} className="flex items-center justify-between gap-3 py-2">
              <div className="text-xs">
                <span className="font-mono text-slate-800">
                  {anchor.referee_email_masked ?? anchor.referee_user_id ?? t.activations.unknown}
                </span>{' '}
                <span className="text-slate-500">
                  {t.activations.activated(fmtDate(anchor.triggered_at))}
                </span>
              </div>
              <Button
                variant="primary"
                size="sm"
                disabled={approveMutation.isPending}
                onClick={() => approveMutation.mutate(anchor.id)}
              >
                {t.activations.approve}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {approveMutation.isError && (
        <div className="mt-2 text-xs text-rose-600">
          {String((approveMutation.error as Error).message)}
        </div>
      )}
    </section>
  )
}

export function ReferralProgramAdmin() {
  const t = useT(referralProgramAdminI18n)
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{t.page.title}</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          {t.page.descBefore} <span className="font-mono">/apply?ref=CODE</span>
          {t.page.descAfter}
        </p>
      </div>
      <RewardsTermsCard />
      <DefaultsCard />
      <CodesCard />
      <ActivationsCard />
    </div>
  )
}
